// Settings stored on the server and shared with the iOS and Android apps:
// work week, billing period, rounding, language, notifications, e-mail options.

import { api } from '../api';
import { session } from './session.svelte';
import type { NotificationSettings, RoundingSettings, WorkSettings } from '../types';

export const DEFAULT_WORK: WorkSettings = {
  week_start_day: 1,
  target_hours_per_week: 40,
  work_days: '1,2,3,4,5',
  quick_time_input: false,
  rounding_settings: {},
  period_type: 'monthly',
  period_start_day: 1,
  period_duration: 1,
  period_default_filter: 'month',
  period_start_date: null,
  language: null
};

export const DEFAULT_ROUNDING: RoundingSettings = {
  rounding_enabled: false,
  rounding_mode: 'nearest',
  rounding_increment: 15,
  rounding_threshold: null,
  apply_scope: 'per_entry',
  min_billable_time: 0,
  grace_period: 0,
  show_original_time: false
};

export const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  browserNotifications: true,
  notifOnTimerStart: false,
  notifOnTimerStop: false,
  notifLongTimer: true,
  notifLongTimerHours: 8,
  notifLongBreak: true,
  notifLongBreakMinutes: 30,
  notifDailySummary: false,
  notifDailySummaryTime: '17:00',
  notifGoalReached: false,
  notifQuietHours: true,
  notifQuietFrom: '22:00',
  notifQuietTo: '08:00'
};

class Account {
  work = $state<WorkSettings>({ ...DEFAULT_WORK });
  notifications = $state<NotificationSettings>({ ...DEFAULT_NOTIFICATIONS });
  email = $state<{ marketing_opt_in: boolean; mfa_email_enabled: boolean }>({ marketing_opt_in: false, mfa_email_enabled: false });
  loaded = $state(false);

  workDays = $derived(
    String(this.work.work_days || '1,2,3,4,5')
      .split(',')
      .map((d) => parseInt(d, 10))
      .filter((d) => d >= 0 && d <= 6)
  );
  /** Target hours for one work day. */
  dailyTarget = $derived(this.workDays.length ? (Number(this.work.target_hours_per_week) || 0) / this.workDays.length : 0);
  rounding = $derived<RoundingSettings>({ ...DEFAULT_ROUNDING, ...(this.work.rounding_settings || {}) });

  async load() {
    const [work, notif, email] = await Promise.allSettled([
      api.get<WorkSettings>('/api/user/work-settings'),
      api.get<{ settings: Partial<NotificationSettings> }>('/api/push/notification-settings'),
      api.get<{ marketing_opt_in: boolean; mfa_email_enabled: boolean }>('/api/settings/email-preferences')
    ]);
    if (work.status === 'fulfilled' && work.value) this.work = { ...DEFAULT_WORK, ...work.value, quick_time_input: !!work.value.quick_time_input };
    if (notif.status === 'fulfilled') this.notifications = { ...DEFAULT_NOTIFICATIONS, ...(notif.value?.settings || {}) };
    if (email.status === 'fulfilled' && email.value) this.email = { marketing_opt_in: !!email.value.marketing_opt_in, mfa_email_enabled: !!email.value.mfa_email_enabled };
    this.loaded = true;
  }

  // Saves run one after another and each sends the latest state, so two quick
  // changes from different screens cannot overwrite each other.
  private workQueue: Promise<unknown> = Promise.resolve();

  saveWork(patch: Partial<WorkSettings>): Promise<void> {
    const previous = this.work;
    this.work = { ...this.work, ...patch };
    const run = this.workQueue.then(async () => {
      try {
        await api.put('/api/user/work-settings', $state.snapshot(this.work));
      } catch (err) {
        // Undo only the fields this call changed.
        const reverted = { ...this.work } as Record<string, unknown>;
        for (const key of Object.keys(patch)) reverted[key] = (previous as unknown as Record<string, unknown>)[key];
        this.work = reverted as unknown as WorkSettings;
        throw err;
      }
    });
    this.workQueue = run.catch(() => {});
    return run;
  }

  async saveRounding(patch: Partial<RoundingSettings>) {
    await this.saveWork({ rounding_settings: { ...this.rounding, ...patch } });
  }

  async saveNotifications(patch: Partial<NotificationSettings>) {
    const next = { ...this.notifications, ...patch };
    await api.put('/api/push/notification-settings', next);
    this.notifications = next;
  }

  async saveEmail(patch: Partial<{ marketing_opt_in: boolean; mfa_email_enabled: boolean }>) {
    const res = await api.post<{ marketing_opt_in: boolean; mfa_email_enabled: boolean }>('/api/settings/email-preferences', patch);
    this.email = { marketing_opt_in: !!res.marketing_opt_in, mfa_email_enabled: !!res.mfa_email_enabled };
  }

  async updateProfile(name: string, email: string) {
    await api.put('/api/user/profile', { name, email });
    if (session.user) session.user = { ...session.user, name, email };
  }

  async changePassword(currentPassword: string, newPassword: string) {
    // 401 here means "wrong current password", not an expired session.
    await api.post('/api/user/change-password', { currentPassword, newPassword }, { allowUnauthorized: true });
  }

  async completeOnboarding() {
    await api.post('/api/user/onboarding-completed');
  }
}

export const account = new Account();

/** Server password policy: ≥ 8 characters with lower case, upper case, digit and symbol. */
export function passwordProblems(pw: string): string[] {
  const problems: string[] = [];
  if (pw.length < 8) problems.push('length');
  if (!/[a-z]/.test(pw)) problems.push('lower');
  if (!/[A-Z]/.test(pw)) problems.push('upper');
  if (!/\d/.test(pw)) problems.push('digit');
  if (!/[^A-Za-z0-9]/.test(pw)) problems.push('symbol');
  return problems;
}
